/**
 * Shared push media controls — the notification image and sound. Used by Compose and Bulk Send so the
 * two can't drift.
 *
 * IMAGE. Uploaded to S3 (public URL) rather than pasted, and constrained to what the providers will
 * actually render:
 *   - FCM (Android/Web) caps notification.image at ~1 MB and shows JPEG/PNG/GIF. That 1 MB is the
 *     binding limit — APNs allows up to 10 MB — so we enforce the smaller one.
 *   - iOS needs a Notification Service Extension in the app to download and attach the image. The
 *     backend now sends mutable-content + the URL, but until the spokes ship that extension the image
 *     will not appear on iPhone. Said plainly below rather than implied.
 *
 * SOUND. Only values that are valid on BOTH platforms are offered. "default" and silent work
 * everywhere; a custom sound needs the file bundled in each app (iOS: app bundle, Android: res/raw),
 * and none are bundled today — offering names that silently fall back to the default would be a lie.
 */

import { IconPhoto, IconTrash, IconUpload, IconInfoCircle } from '@tabler/icons-react';
import { FileButton } from '@mantine/core';
import { Stack, Group, Text, Button, Select, Alert, ActionIcon, Image } from '../../../../components/ui';
import { useFileUpload } from '../../../../components/ccms-components/file-management/hooks';
import { toast } from '../../../../components/ui/providers/AlertProvider';

/** FCM rejects/skips notification images above ~1 MB, so that is the cross-platform ceiling. */
export const PUSH_IMAGE_MAX_BYTES = 1024 * 1024;
export const PUSH_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif'];

/** Sentinel for "no sound". The backend omits the key entirely for a silent push. */
export const SOUND_SILENT = 'none';

/** Only sounds that behave identically on iOS and Android. */
export const PUSH_SOUND_OPTIONS = [
  { value: 'default', label: 'Default — the device notification sound (iOS + Android)' },
  { value: SOUND_SILENT, label: 'Silent — deliver with no sound (iOS + Android)' },
];

export function PushImageField({
  value,
  onChange,
  recordId,
}: {
  value: string;
  onChange: (url: string) => void;
  /** Groups the uploads in FileManagement (e.g. 'push-compose' / 'push-bulk'). */
  recordId: string;
}) {
  const { upload, uploading } = useFileUpload({
    domain: 'Communications',
    recordId,
    maxSize: PUSH_IMAGE_MAX_BYTES,
    allowedTypes: PUSH_IMAGE_TYPES,
    onComplete: (files) => {
      const url = files[0]?.url;
      if (url) {
        onChange(url);
        toast.success('Image uploaded');
      } else {
        // A stored file with no public URL can never be fetched by the device.
        toast.error('Upload succeeded but returned no public URL');
      }
    },
    onError: (e) => toast.error(e?.message || 'Image upload failed'),
  });

  return (
    <Stack gap={6}>
      <Text size="sm" fw={500}>Image (optional)</Text>
      <Text size="xs" c="dimmed">
        JPEG, PNG or GIF, up to 1&nbsp;MB — the limit Android/FCM will actually render. Uploaded to S3;
        the public link is what gets sent.
      </Text>

      {value ? (
        <Group gap="sm" align="center" wrap="nowrap">
          <Image src={value} w={64} h={64} fit="cover" radius="sm" />
          <Text size="xs" c="dimmed" style={{ flex: 1, wordBreak: 'break-all' }}>{value}</Text>
          <ActionIcon variant="subtle" color="red" title="Remove image" onClick={() => onChange('')}>
            <IconTrash size={16} />
          </ActionIcon>
        </Group>
      ) : (
        <Group gap="xs">
          <FileButton onChange={(file) => file && upload([file])} accept={PUSH_IMAGE_TYPES.join(',')}>
            {(props) => (
              <Button
                {...props}
                variant="light"
                leftSection={uploading ? <IconUpload size={16} /> : <IconPhoto size={16} />}
                loading={uploading}
              >
                Upload image
              </Button>
            )}
          </FileButton>
        </Group>
      )}

      <Alert color="yellow" icon={<IconInfoCircle size={16} />} p="xs">
        <Text size="xs">
          Shows on Android and Web. <b>iPhone will not display it</b> until the apps ship a Notification
          Service Extension — the push carries the image, but iOS needs that extension to attach it.
        </Text>
      </Alert>
    </Stack>
  );
}

export function PushSoundField({
  value,
  onChange,
}: {
  value: string;
  onChange: (sound: string) => void;
}) {
  return (
    <Select
      label="Sound (optional)"
      description="Only sounds that behave the same on iOS and Android. A custom sound needs the file bundled in each app (iOS app bundle / Android res/raw); none are today."
      data={PUSH_SOUND_OPTIONS}
      value={value || 'default'}
      onChange={(v) => onChange(v || 'default')}
      allowDeselect={false}
    />
  );
}
